import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, message } from "antd";
import { env } from "../../../env/env";
import YextApi, { yextErrorMessage } from "../../../api/YextApi";
import "./ProfileVisualAssetsSection.scss";

interface UploadedFile {
  id: string;
  name: string;
  url: string;
}

function fileFromUrl(url: string): UploadedFile {
  return { id: url, name: url.split("/").pop() ?? url, url };
}

interface Props {
  locationId?: number;
  onDirtyChange?: (dirty: boolean) => void;
}

const BRAND_KIT_PATH = env.dashboardPath + "/account/brand-management";

export function ProfileVisualAssetsSection({ locationId, onDirtyChange }: Props) {
  // Both sections are read-only — sourced from the location's brand kit (managed in Brand Kit & Assets).
  const [logo, setLogo] = useState<UploadedFile[]>([]);
  const [gallery, setGallery] = useState<UploadedFile[]>([]);
  const [saving, setSaving] = useState(false);

  // Nothing is editable here, so the section is never dirty.
  useEffect(() => {
    onDirtyChange?.(false);
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      const v = data.visualAssets;
      setLogo(v?.logo ? [fileFromUrl(v.logo)] : []);
      setGallery((v?.photoGallery ?? []).map(fileFromUrl));
    });
  }, [locationId]);

  // Brand kit edits don't auto-sync to Yext, so this pushes the current brand assets on demand.
  async function pushToYext() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      await YextApi.syncLocation(locationId, {});
      message.success("Visual assets pushed to Yext.");
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to push visual assets."));
    } finally {
      setSaving(false);
    }
  }

  const brandKitHint = (
    <p className="pva-gallery-hint">
      Managed in{" "}
      <Link className="pva-gallery-link" to={BRAND_KIT_PATH}>
        Brand Kit &amp; Assets
      </Link>
      .
    </p>
  );

  return (
    <div className="profile-visual-assets">
      <h3 className="pva-title">Visual Assets</h3>

      <section className="pva-group">
        <span className="pva-group-label">Logo</span>
        {logo.length > 0 ? (
          <div className="pva-thumbs single">
            {logo.map((f) => (
              <div className="pva-thumb readonly" key={f.id}>
                <img src={f.url} alt={f.name} />
              </div>
            ))}
          </div>
        ) : (
          <p className="pva-gallery-empty">No logo in this location's brand kit yet.</p>
        )}
        {brandKitHint}
      </section>

      <section className="pva-group">
        <span className="pva-group-label">Photo gallery</span>
        {gallery.length > 0 ? (
          <div className="pva-thumbs multi">
            {gallery.map((f) => (
              <div className="pva-thumb readonly" key={f.id}>
                <img src={f.url} alt={f.name} />
              </div>
            ))}
          </div>
        ) : (
          <p className="pva-gallery-empty">No photos in this location's brand kit yet.</p>
        )}
        {brandKitHint}
      </section>

      <footer className="pva-actions">
        <Button
          type="primary"
          danger
          className="pva-save-btn"
          size="large"
          onClick={pushToYext}
          loading={saving}
        >
          Save Changes
        </Button>
      </footer>
    </div>
  );
}
